import type { Game } from '../shared/types';

export const ISLAND_ART_ROOT = '/art/island-atlas-v3';
export const ISLAND_COVER_ROOT = '/art/island-atlas-v8';
export const ISLAND_SCENE_ROOT = '/art/island-atlas-v4';
export const ISLAND_TERRAIN = {
  src: '/art/island-atlas-v9/island-terrain.webp',
  width: 1774,
  height: 887,
} as const;
export type IslandIcon =
  | 'sheep'
  | 'plane'
  | 'snow'
  | 'bubbles'
  | 'cards'
  | 'tiles'
  | 'puzzle'
  | 'fruit'
  | 'pool'
  | 'flower'
  | 'town'
  | 'dog'
  | 'fish';
export interface IslandPlace {
  slug: string;
  name: string;
  icon: IslandIcon;
  x: number;
  y: number;
  featured?: boolean;
}

// Ground anchors are percentages of the complete terrain asset.
// Buildings are independent sprites; catalog changes never alter the terrain.
export const ISLAND_PLACES: readonly IslandPlace[] = [
  { slug: 'alpine-rush-2', name: '雪顶山', icon: 'snow', x: 44, y: 24, featured: true },
  { slug: 'skybreaker-stage1', name: '海风机场', icon: 'plane', x: 27, y: 30 },
  { slug: 'bubble-island', name: '泡泡洞湾', icon: 'bubbles', x: 69, y: 28 },
  { slug: 'sheep-meadow', name: '绵羊牧场', icon: 'sheep', x: 39, y: 41, featured: true },
  { slug: 'sunlight-garden-defense', name: '向阳花园', icon: 'flower', x: 56, y: 39 },
  { slug: 'fruit-island', name: '橘子果园', icon: 'fruit', x: 79, y: 40, featured: true },
  { slug: 'sudoku', name: '灯塔书房', icon: 'puzzle', x: 12, y: 43 },
  { slug: 'bamboo-mahjong', name: '竹林茶舍', icon: 'tiles', x: 27, y: 48 },
  { slug: 'jade-doudizhu', name: '翡翠牌馆', icon: 'cards', x: 44, y: 55 },
  { slug: 'shiba-hide-seek', name: '汪汪事务所', icon: 'dog', x: 88, y: 57 },
  { slug: 'lumen-angler', name: '浮光码头', icon: 'fish', x: 25, y: 65, featured: true },
  { slug: 'rich-island', name: '海港小镇', icon: 'town', x: 43, y: 72 },
  { slug: 'midnight-pool', name: '月色球馆', icon: 'pool', x: 65, y: 62, featured: true },
];

export function islandPlace(slug: string) {
  const canonical = /^sheep-meadow(?:-\d+)?$/.test(slug)
    ? 'sheep-meadow'
    : slug === 'bubble-carnival'
      ? 'bubble-island'
      : slug;
  return ISLAND_PLACES.find((place) => place.slug === canonical);
}

export const ISLAND_REGISTRY_KEY = 'moyu.island-plots.v3';
export const PLOTS_PER_DISTRICT = 16;
const PLOTS = [
  ...ISLAND_PLACES.map(({ x, y }) => ({ x, y })),
  { x: 54, y: 57 },
  { x: 55, y: 71 },
  { x: 77, y: 51 },
];
export type IslandRegistry = Record<string, number>;
export interface IslandEntry {
  game: Game;
  place: IslandPlace;
  model: IslandIcon | 'game';
  slot: number;
  district: number;
}

export function readIslandRegistry(value: string | null): IslandRegistry {
  try {
    const parsed: unknown = JSON.parse(value || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([key, slot]) =>
          key.length > 0 &&
          key.length < 256 &&
          typeof slot === 'number' &&
          Number.isInteger(slot) &&
          slot >= ISLAND_PLACES.length &&
          slot < 100000,
      ),
    );
  } catch {
    return {};
  }
}

export function planIsland(games: readonly Game[], previous: IslandRegistry = {}) {
  // Retain empty allocations so unpublishing a game never shifts its neighbours.
  const registry: IslandRegistry = Object.create(null);
  const occupied = new Set<number>();
  for (const [key, slot] of Object.entries(readIslandRegistry(JSON.stringify(previous)))) {
    if (!occupied.has(slot)) {
      registry[key] = slot;
      occupied.add(slot);
    }
  }
  const knownOccupied = new Set<number>();
  const entries: IslandEntry[] = [];
  const ordered = games
    .filter((game) => game.published)
    .slice()
    .sort(
      (a, b) =>
        (a.createdAt || '').localeCompare(b.createdAt || '') ||
        a.slug.localeCompare(b.slug) ||
        a.id.localeCompare(b.id),
    );
  for (const game of ordered) {
    const known = islandPlace(game.slug);
    const preferred = known ? ISLAND_PLACES.indexOf(known) : -1;
    const useKnown = preferred >= 0 && !knownOccupied.has(preferred);
    let slot = preferred;
    if (useKnown) knownOccupied.add(preferred);
    else {
      const key = known ? game.slug + ':' + game.id : game.slug;
      slot = registry[key];
      if (slot === undefined) {
        slot = ISLAND_PLACES.length;
        while (occupied.has(slot)) slot++;
        registry[key] = slot;
        occupied.add(slot);
      }
    }
    const anchor = PLOTS[slot % PLOTS_PER_DISTRICT];
    entries.push({
      game,
      slot,
      district: Math.floor(slot / PLOTS_PER_DISTRICT),
      model: known?.icon || 'game',
      place: known
        ? { ...known, ...anchor }
        : { slug: game.slug, name: '游戏小屋', icon: 'town', ...anchor },
    });
  }
  return { entries, registry };
}

export function islandGames(games: readonly Game[]) {
  return planIsland(games).entries;
}

export function gameDestination(game: Pick<Game, 'id' | 'slug' | 'free'>, member = false) {
  return game.free || member ? '/play/' + game.id : '/game/' + game.slug;
}
